import { useMemo } from 'react';
import { ClusterMap, escapeHtml, type MapMarker } from './ClusterMap';
import { categoryOf, UNKNOWN_CATEGORY_COLOR, type PerformanceCategory } from './useTakataDealers';
import type { Dealer } from './useDealers';

/**
 * The main dealer map — `_Map.cshtml` plus the map half of `stla_dashboard.js`.
 *
 * Unlike the Takata map this one plots the rows as they come, one pin per dealer
 * per brand, and keeps its configured view instead of re-framing on every filter
 * change. Both behaviours are the Razor original's; they differ between the two
 * screens because the maps were written separately, not because either is wrong.
 *
 * The cluster bubbles are `leaflet.markercluster`'s own, again as before — this
 * map never supplied an `iconCreateFunction`.
 */

/** Europe, at the view `stla_dashboard.js` configured. */
const INITIAL_CENTER: [number, number] = [50.0, 10.0];
const INITIAL_ZOOM = 4;
const MIN_ZOOM = 3;
const MAX_ZOOM = 15;
const MARKER_SIZE = 30;

export interface StellantisDealerMapLabels {
  brand: string;
  city: string;
  address: string;
  phone: string;
  averageTime: string;
  calls: string;
  answerRate: string;
  days: string;
  noAppointment: string;
}

export interface StellantisDealerMapProps {
  dealers: Dealer[];
  categories: Record<string, PerformanceCategory>;
  labels: StellantisDealerMapLabels;
  resizeToken?: unknown;
}

function popupHtml(
  dealer: Dealer,
  categories: Record<string, PerformanceCategory>,
  labels: StellantisDealerMapLabels,
): string {
  const category = categoryOf(categories, dealer.categoryIndex);
  const answerRate = dealer.totalCalls > 0 ? (dealer.answeredCalls / dealer.totalCalls) * 100 : 0;

  // Category 0 is the no-appointment case, and a null wait means the same thing;
  // either way the tile must not read "0.0 days".
  const booked = (dealer.categoryIndex ?? 0) > 0 && dealer.averageWaitDays != null;
  const averageTime = booked
    ? `${(dealer.averageWaitDays ?? 0).toFixed(1)} ${escapeHtml(labels.days)}`
    : escapeHtml(labels.noAppointment);

  return `
    <div style="min-width: 200px;">
      <h4 style="margin: 0 0 0.5rem 0;">${escapeHtml(dealer.name)}</h4>
      <p style="margin: 0.25rem 0;"><strong>${escapeHtml(labels.brand)}:</strong> ${escapeHtml(dealer.brand)}</p>
      <p style="margin: 0.25rem 0;"><strong>${escapeHtml(labels.city)}:</strong> ${escapeHtml(dealer.city)}, ${escapeHtml(dealer.country)}</p>
      <p style="margin: 0.25rem 0;"><strong>${escapeHtml(labels.address)}:</strong> ${escapeHtml(dealer.address)}</p>
      <p style="margin: 0.25rem 0;"><strong>${escapeHtml(labels.phone)}:</strong> ${escapeHtml(dealer.phone)}</p>
      <hr style="margin: 0.5rem 0;">
      <p style="margin: 0.25rem 0;"><strong>${escapeHtml(labels.averageTime)}:</strong> ${averageTime}</p>
      <p style="margin: 0.25rem 0;"><strong>${escapeHtml(labels.calls)}:</strong> ${escapeHtml(dealer.answeredCalls)}/${escapeHtml(dealer.totalCalls)}</p>
      <p style="margin: 0.25rem 0;"><strong>${escapeHtml(labels.answerRate)}:</strong> ${answerRate.toFixed(1)}%</p>
      <p style="margin: 0.25rem 0;">
        <span style="display: inline-block; padding: 0.25rem 0.5rem; border-radius: 4px;
                     background-color: ${escapeHtml(category?.backgroundColor ?? UNKNOWN_CATEGORY_COLOR)};
                     color: white; font-size: 0.8rem;">
          ${escapeHtml(category?.label ?? 'N/A')}
        </span>
      </p>
    </div>`;
}

export function StellantisDealerMap({
  dealers,
  categories,
  labels,
  resizeToken,
}: StellantisDealerMapProps) {
  const markers = useMemo<MapMarker[]>(
    () =>
      dealers.map((dealer, index) => ({
        id: `${dealer.physOut}-${dealer.brand}-${index}`,
        latitude: dealer.latitude,
        longitude: dealer.longitude,
        color:
          categoryOf(categories, dealer.categoryIndex)?.backgroundColor ?? UNKNOWN_CATEGORY_COLOR,
        size: MARKER_SIZE,
        categoryIndex: dealer.categoryIndex ?? 0,
        popupHtml: () => popupHtml(dealer, categories, labels),
      })),
    [dealers, categories, labels],
  );

  return (
    <ClusterMap
      markers={markers}
      center={INITIAL_CENTER}
      zoom={INITIAL_ZOOM}
      minZoom={MIN_ZOOM}
      maxZoom={MAX_ZOOM}
      resizeToken={resizeToken}
    />
  );
}
